import React, { useMemo, useState } from "react";
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  Handle,
  Position,
  BackgroundVariant,
  ReactFlowProvider,
} from "@xyflow/react";
import { ChevronDown, ChevronRight } from "lucide-react";
import "@xyflow/react/dist/style.css";
import { Maximize2 } from "lucide-react";
import heartLoading from "../../../assests/heartLoading.gif";

const getScoreColor = (score) => {
  const s = parseFloat(score) || 0;
  if (s >= 95) return "#22c55e";
  if (s >= 90) return "#f59e0b";
  return "#ef4444";
};

const ScoreDot = ({ score, size = 9 }) => (
  <div style={{
    width: size, height: size, borderRadius: "50%",
    backgroundColor: getScoreColor(score), flexShrink: 0,
    border: "1.5px solid rgba(0,0,0,0.12)",
  }} />
);

const getNodeOpacity = (data) => (data?.dimmed ? 0.35 : 1);
const getNodeScale = (data) => (data?.highlighted ? 1.02 : 1);
const normalizeKey = (value) => String(value || "").trim().toLowerCase();

const NodeBadge = ({ color = "#1d4ed8" }) => (
  <span
    style={{
      width: 8,
      height: 8,
      borderRadius: 2,
      background: color,
      display: "inline-block",
      flexShrink: 0,
    }}
  />
);

const truncateMultilineStyle = {
  overflow: "hidden",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
};

const KpiKpiNode = ({ data }) => {
  const isExpanded = data?.expanded;
  return (
    <div
      style={{
        background: "#ffffff",
        border: data?.highlighted ? "2px solid #1d4ed8" : "1px solid #c9dbff",
        borderRadius: 10,
        overflow: "hidden",
        minWidth: 220,
        maxWidth: 250,
        boxShadow: data?.highlighted
          ? "0 6px 16px rgba(29,78,216,0.20)"
          : "0 3px 10px rgba(30,41,59,0.10)",
        opacity: getNodeOpacity(data),
        transform: `scale(${getNodeScale(data)})`,
        cursor: "pointer",
      }}
    >
      <div
        style={{
          background: "#1e3a8a",
          color: "#ffffff",
          padding: "7px 10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700 }}>
          <NodeBadge color="#93c5fd" />
          KPI
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <ScoreDot score={data.score} size={8} />
          <button
            onClick={(e) => {
              e.stopPropagation();
              data?.onChevronClick?.();
            }}
            style={{
              background: "none",
              border: "none",
              color: "#ffffff",
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              width: 14,
              height: 14,
              flexShrink: 0,
            }}
            title={isExpanded ? "Collapse KPI" : "Expand KPI"}
          >
            {isExpanded ? <ChevronDown size={12} strokeWidth={2.5} /> : <ChevronRight size={12} strokeWidth={2.5} />}
          </button>
        </span>
      </div>
      <div style={{ padding: "9px 10px", background: "#eff6ff" }}>
        <div style={{ fontSize: 11, color: "#1e3a8a", lineHeight: 1.35, ...truncateMultilineStyle }} title={data.name}>
          {data.name}
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{ background: "#93c5fd", width: 6, height: 6, border: "none" }}
      />
    </div>
  );
};

const KpiProductNode = ({ data }) => {
  const isExpanded = data?.expanded;
  return (
    <div
      style={{
        background: "#ffffff",
        border: data?.highlighted ? "2px solid #0f766e" : "1px solid #99f6e4",
        borderRadius: 10,
        overflow: "hidden",
        minWidth: 220,
        maxWidth: 280,
        boxShadow: data?.highlighted
          ? "0 6px 16px rgba(15,118,110,0.20)"
          : "0 3px 10px rgba(30,41,59,0.10)",
        opacity: getNodeOpacity(data),
        transform: `scale(${getNodeScale(data)})`,
      }}
    >
      <div
        style={{
          background: "#0f766e",
          color: "#ffffff",
          padding: "7px 10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700 }}>
          <NodeBadge color="#99f6e4" />
          Product
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            data?.onChevronClick?.();
          }}
          style={{
            background: "none",
            border: "none",
            color: "#ffffff",
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            width: 14,
            height: 14,
            flexShrink: 0,
          }}
          title={isExpanded ? "Collapse Product" : "Expand Product"}
        >
          {isExpanded ? <ChevronDown size={12} strokeWidth={2.5} /> : <ChevronRight size={12} strokeWidth={2.5} />}
        </button>
      </div>
      <div style={{ padding: "9px 10px", background: "#f0fdfa" }}>
        <div style={{ fontSize: 11, color: "#0f766e", lineHeight: 1.35, ...truncateMultilineStyle }} title={data.name}>
          {data.name}
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{ background: "#99f6e4", width: 6, height: 6, border: "none" }}
      />
    </div>
  );
};

const KpiSubAssetNode = ({ data }) => {
  const isExpanded = data?.expanded;
  return (
    <div
      style={{
        background: "#ffffff",
        border: data?.highlighted ? "2px solid #15803d" : "1px solid #bae6c8",
        borderRadius: 10,
        overflow: "hidden",
        minWidth: 230,
        maxWidth: 280,
        boxShadow: "0 3px 10px rgba(15,23,42,0.10)",
        opacity: getNodeOpacity(data),
        transform: `scale(${getNodeScale(data)})`,
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        style={{ background: "#86efac", width: 6, height: 6, border: "none" }}
      />
      <div
        style={{
          background: "#15803d",
          color: "#fff",
          padding: "7px 10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700 }}>
          <NodeBadge color="#bbf7d0" />
          Asset
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <ScoreDot score={data.score} size={8} />
          <button
            onClick={(e) => {
              e.stopPropagation();
              data?.onChevronClick?.();
            }}
            style={{
              background: "none",
              border: "none",
              color: "#fff",
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              width: 14,
              height: 14,
              flexShrink: 0,
            }}
            title={isExpanded ? "Collapse Asset" : "Expand Asset"}
          >
            {isExpanded ? <ChevronDown size={12} strokeWidth={2.5} /> : <ChevronRight size={12} strokeWidth={2.5} />}
          </button>
        </span>
      </div>
      <div style={{ padding: "9px 10px", background: "#ecfdf5" }}>
        <div style={{ fontSize: 11, color: "#14532d", lineHeight: 1.35, ...truncateMultilineStyle }} title={data.name}>
          {data.name}
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{ background: "#86efac", width: 6, height: 6, border: "none" }}
      />
    </div>
  );
};

const KpiMainAssetNode = ({ data }) => {
  const isExpanded = data?.expanded;
  return (
    <div
      style={{
        background: "#ffffff",
        border: data?.highlighted ? "2px solid #15803d" : "1px solid #86efac",
        borderRadius: 10,
        overflow: "hidden",
        minWidth: 250,
        maxWidth: 310,
        boxShadow: data?.highlighted
          ? "0 6px 18px rgba(21,128,61,0.22)"
          : "0 3px 10px rgba(15,23,42,0.10)",
        opacity: getNodeOpacity(data),
        transform: `scale(${getNodeScale(data)})`,
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        style={{ background: "#86efac", width: 6, height: 6, border: "none" }}
      />
      <div
        style={{
          background: "#15803d",
          color: "#ffffff",
          padding: "7px 10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700 }}>
          <NodeBadge color="#bbf7d0" />
          Asset
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <ScoreDot score={data.score} size={8} />
          <button
            onClick={(e) => {
              e.stopPropagation();
              data?.onChevronClick?.();
            }}
            style={{
              background: "none",
              border: "none",
              color: "#ffffff",
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              width: 14,
              height: 14,
              flexShrink: 0,
            }}
            title={isExpanded ? "Collapse Asset" : "Expand Asset"}
          >
            {isExpanded ? <ChevronDown size={12} strokeWidth={2.5} /> : <ChevronRight size={12} strokeWidth={2.5} />}
          </button>
        </span>
      </div>
      <div style={{ padding: "9px 10px", background: "#ecfdf5" }}>
        <div style={{ fontSize: 11, color: "#14532d", lineHeight: 1.35, ...truncateMultilineStyle }} title={data.name}>
          {data.name}
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{ background: "#86efac", width: 6, height: 6, border: "none" }}
      />
    </div>
  );
};

const KpiColumnNode = ({ data }) => {
  return (
    <div
      style={{
        background: data?.highlighted ? "#dbeafe" : "#ffffff",
        border: data?.highlighted
          ? "1.8px solid #2563eb"
          : data.isMain
          ? "1px solid #bfdbfe"
          : "1px solid #e2e8f0",
        borderRadius: 10,
        overflow: "hidden",
        minWidth: 210,
        maxWidth: 260,
        boxShadow: data?.highlighted
          ? "0 4px 12px rgba(37,99,235,0.22)"
          : "0 2px 8px rgba(30,41,59,0.10)",
        opacity: data?.dimmed ? 0.25 : 1,
        transform: `scale(${getNodeScale(data)})`,
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        style={{ background: "#93c5fd", width: 6, height: 6, border: "none" }}
      />
      <div
        style={{
          background: "#60a5fa",
          color: "#ffffff",
          padding: "7px 10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700 }}>
          <NodeBadge color="#dbeafe" />
          Column
        </span>
        <ScoreDot score={data.score} size={8} />
      </div>
      <div
        style={{
          padding: "9px 10px",
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: data?.highlighted ? "#eff6ff" : "#f8fbff",
        }}
      >
        <span
          style={{
            fontSize: 11,
            color: "#1e3a8a",
            flex: 1,
            wordBreak: "break-all",
            lineHeight: 1.35,
            ...truncateMultilineStyle,
          }}
          title={data.name}
        >
          {data.name}
        </span>
      </div>
    </div>
  );
};

const kpiNodeTypes = {
  kpiProductNode: KpiProductNode,
  kpiKpiNode: KpiKpiNode,
  kpiSubAssetNode: KpiSubAssetNode,
  kpiMainAssetNode: KpiMainAssetNode,
  kpiColumnNode: KpiColumnNode,
};

const makeEdge = (id, source, target, color = "#64748b", kind = "generic") => ({
  id,
  source,
  target,
  sourceHandle: "right",
  targetHandle: "left",
  type: "step",
  style: { stroke: color, strokeWidth: 1.5, strokeDasharray: "4 4", opacity: 0.85 },
  markerEnd: { type: "arrowclosed", color, width: 13, height: 13 },
  data: { kind },
});

const getAssetKpis = (assetOrData) => {
  if (Array.isArray(assetOrData?.kpis)) return assetOrData.kpis;
  if (Array.isArray(assetOrData?.upstreams)) {
    return assetOrData.upstreams.map((kpi) => ({
      ...kpi,
      column: Array.isArray(kpi?.column) ? kpi.column : [],
    }));
  }
  return [];
};

const getAssetColumns = (assetOrData) => {
  // Glossary mode: columns are directly on the asset
  if (Array.isArray(assetOrData?.columns) && assetOrData.columns.length > 0) {
    return assetOrData.columns;
  }

  // Fallback: columns from downstreams (for backward compatibility)
  if (Array.isArray(assetOrData?.downstreams) && assetOrData.downstreams.length > 0) {
    return assetOrData.downstreams;
  }

  // Fallback: derive from KPI column arrays
  const derived = new Map();
  getAssetKpis(assetOrData).forEach((kpi) => {
    (kpi.column || []).forEach((col) => {
      const key = normalizeKey(col?.column_name);
      if (key && !derived.has(key)) derived.set(key, col);
    });
  });
  return Array.from(derived.values());
};

const buildSingleAssetGraph = (data) => {
  const KPI_X = 0;
  const ASSET_X = 560;
  const COL_X = 1120;
  const COL_SPACING = 52;
  const KPI_MIN_SPACING = 90;

  const nodes = [];
  const edges = [];
  const kpiToColumnNodeIds = {};
  const kpiToAssetNodeIds = {};

  const kpis = getAssetKpis(data);
  const columns = getAssetColumns(data);

  const colTotalHeight = Math.max(0, (columns.length - 1) * COL_SPACING);
  const kpiTotalHeight = Math.max(0, (kpis.length - 1) * KPI_MIN_SPACING);
  const sectionHeight = Math.max(colTotalHeight, kpiTotalHeight);
  const mainAssetY = sectionHeight / 2;
  const kpiStartY = (sectionHeight - kpiTotalHeight) / 2;
  const colStartY = (sectionHeight - colTotalHeight) / 2;

  const mainAssetId = "main-asset-node";
  nodes.push({
    id: mainAssetId, type: "kpiMainAssetNode",
    position: { x: ASSET_X, y: mainAssetY },
    data: {
      name: data.asset_name, score: data.asset_score,
      assetId: "main", hasColumns: columns.length > 0, isExpanded: false,
    },
  });

  const colNameToNodeId = new Map();
  columns.forEach((col, j) => {
    const normalized = normalizeKey(col?.column_name);
    const colId = `main-col-${j}-${normalized || "unknown"}`;
    colNameToNodeId.set(normalized, colId);
    nodes.push({
      id: colId,
      type: "kpiColumnNode",
      position: { x: COL_X, y: colStartY + j * COL_SPACING },
      data: { name: col.column_name, score: col.column_score, isMain: true },
    });
    edges.push(makeEdge(`e-main-col-${j}`, mainAssetId, colId, "#64748b", "asset-to-column"));
  });

  kpis.forEach((kpi, i) => {
    const kpiId = `kpi-node-${i}`;
    nodes.push({
      id: kpiId,
      type: "kpiKpiNode",
      position: { x: KPI_X, y: kpiStartY + i * KPI_MIN_SPACING },
      data: { name: kpi.kpi_name, score: kpi.kpi_score },
    });
    edges.push(makeEdge(`e-kpi-node-${i}->main`, kpiId, mainAssetId, "#64748b", "kpi-to-asset"));

    kpiToAssetNodeIds[kpiId] = new Set([mainAssetId]);
    const relatedCols = new Set();
    (kpi.column || []).forEach((col) => {
      const colId = colNameToNodeId.get(normalizeKey(col?.column_name));
      if (colId) relatedCols.add(colId);
    });
    kpiToColumnNodeIds[kpiId] = relatedCols;
  });

  return { nodes, edges, kpiToColumnNodeIds, kpiToAssetNodeIds };
};

const buildMultiAssetGraph = (data) => {
  const KPI_X = 0;
  const ASSET_X = 560;
  const COL_X = 1120;
  const KPI_SPACING = 90;
  const ASSET_SPACING = 210;
  const COL_SPACING = 42;

  const assets = Array.isArray(data?.assets) ? data.assets : [];
  const nodes = [];
  const edges = [];
  const kpiToColumnNodeIds = {};
  const kpiToAssetNodeIds = {};

  const kpiByName = new Map();
  assets.forEach((asset) => {
    getAssetKpis(asset).forEach((kpi) => {
      const key = normalizeKey(kpi?.kpi_name);
      if (key && !kpiByName.has(key)) {
        kpiByName.set(key, { name: kpi.kpi_name, score: kpi.kpi_score });
      }
    });
  });

  const kpiNodeIdByName = new Map();
  Array.from(kpiByName.entries()).forEach(([key, kpi], i) => {
    const kpiId = `kpi-node-${i}-${key}`;
    kpiNodeIdByName.set(key, kpiId);
    kpiToColumnNodeIds[kpiId] = new Set();
    kpiToAssetNodeIds[kpiId] = new Set();
    nodes.push({
      id: kpiId,
      type: "kpiKpiNode",
      position: { x: KPI_X, y: i * KPI_SPACING },
      data: { name: kpi.name, score: kpi.score },
    });
  });

  const kpiTotalHeight = Math.max(0, (kpiByName.size - 1) * KPI_SPACING);
  const assetsTotalHeight = Math.max(0, (assets.length - 1) * ASSET_SPACING);
  const baseOffsetY = Math.max(0, (assetsTotalHeight - kpiTotalHeight) / 2);

  const edgeSet = new Set();
  assets.forEach((asset, assetIdx) => {
    const assetNodeId = `asset-${asset.asset_id || assetIdx}-${assetIdx}`;
    const assetName = asset.product_name
      ? `${asset.product_name} / ${asset.asset_name}`
      : asset.asset_name;

    const assetY = assetIdx * ASSET_SPACING;
    nodes.push({
      id: assetNodeId,
      type: "kpiMainAssetNode",
      position: { x: ASSET_X, y: assetY },
      data: { name: assetName, score: asset.asset_score },
    });

    const columns = getAssetColumns(asset);
    const colNameToNodeId = new Map();
    const colTotalHeight = Math.max(0, (columns.length - 1) * COL_SPACING);
    const colStartY = assetY - colTotalHeight / 2;

    columns.forEach((col, colIdx) => {
      const key = normalizeKey(col?.column_name);
      const colId = `col-${asset.asset_id || assetIdx}-${assetIdx}-${colIdx}-${key || "unknown"}`;
      colNameToNodeId.set(key, colId);

      nodes.push({
        id: colId,
        type: "kpiColumnNode",
        position: { x: COL_X, y: colStartY + colIdx * COL_SPACING },
        data: { name: col.column_name, score: col.column_score, isMain: true },
      });

      edges.push(makeEdge(`e-${assetNodeId}->${colId}`, assetNodeId, colId, "#64748b", "asset-to-column"));
    });

    getAssetKpis(asset).forEach((kpi) => {
      const kpiId = kpiNodeIdByName.get(normalizeKey(kpi?.kpi_name));
      if (!kpiId) return;

      const edgeKey = `${kpiId}->${assetNodeId}`;
      if (!edgeSet.has(edgeKey)) {
        edgeSet.add(edgeKey);
        edges.push(makeEdge(`e-${edgeKey}`, kpiId, assetNodeId, "#64748b", "kpi-to-asset"));
      }

      kpiToAssetNodeIds[kpiId].add(assetNodeId);
      (kpi.column || []).forEach((col) => {
        const colId = colNameToNodeId.get(normalizeKey(col?.column_name));
        if (colId) kpiToColumnNodeIds[kpiId].add(colId);
      });
    });
  });

  const normalizedNodes = nodes.map((node) => {
    if (node.type !== "kpiKpiNode") return node;
    return {
      ...node,
      position: { ...node.position, y: node.position.y + baseOffsetY },
    };
  });

  return {
    nodes: normalizedNodes,
    edges,
    kpiToColumnNodeIds,
    kpiToAssetNodeIds,
  };
};

const buildKpiLineageGraph = (data) => {
  if (!data) {
    return { nodes: [], edges: [], kpiToColumnNodeIds: {}, kpiToAssetNodeIds: {} };
  }

  if (Array.isArray(data.nodes) && Array.isArray(data.edges)) {
    return {
      nodes: data.nodes,
      edges: data.edges,
      kpiToColumnNodeIds: data.kpiToColumnNodeIds || {},
      kpiToAssetNodeIds: data.kpiToAssetNodeIds || {},
    };
  }

  if (Array.isArray(data.assets)) {
    return buildMultiAssetGraph(data);
  }

  return buildSingleAssetGraph(data);
};

const buildProductAssetColumnGraph = (data) => {
  const PRODUCT_X = 0;
  const ASSET_X = 560;
  const COL_X = 1120;
  const PRODUCT_SPACING = 250;
  const ASSET_SPACING = 180;
  const COL_SPACING = 42;

  const products = Array.isArray(data?.products) ? data.products : [];
  const nodes = [];
  const edges = [];
  const productToAssetNodeIds = {};
  const assetToColumnNodeIds = {};

  products.forEach((product, productIdx) => {
    const productId = `product-${productIdx}-${normalizeKey(product?.product_name) || "unknown"}`;
    const productY = productIdx * PRODUCT_SPACING;

    nodes.push({
      id: productId,
      type: "kpiProductNode",
      position: { x: PRODUCT_X, y: productY },
      data: { name: product.product_name },
    });

    productToAssetNodeIds[productId] = new Set();

    const assets = Array.isArray(product?.assets) ? product.assets : [];
    const assetTotalHeight = Math.max(0, (assets.length - 1) * ASSET_SPACING);
    const assetStartY = productY - assetTotalHeight / 2;

    assets.forEach((asset, assetIdx) => {
      const assetId = `product-${productIdx}-asset-${asset.asset_id || assetIdx}`;
      const assetY = assetStartY + assetIdx * ASSET_SPACING;

      nodes.push({
        id: assetId,
        type: "kpiMainAssetNode",
        position: { x: ASSET_X, y: assetY },
        data: { name: asset.asset_name, score: asset.asset_score },
      });

      productToAssetNodeIds[productId].add(assetId);
      edges.push(makeEdge(`e-${productId}->${assetId}`, productId, assetId, "#0f766e", "product-to-asset"));

      assetToColumnNodeIds[assetId] = new Set();
      const columns = Array.isArray(asset?.columns) ? asset.columns : [];
      const colTotalHeight = Math.max(0, (columns.length - 1) * COL_SPACING);
      const colStartY = assetY - colTotalHeight / 2;

      columns.forEach((col, colIdx) => {
        const colId = `product-${productIdx}-asset-${asset.asset_id || assetIdx}-col-${colIdx}-${normalizeKey(
          col?.column_name
        ) || "unknown"}`;

        nodes.push({
          id: colId,
          type: "kpiColumnNode",
          position: { x: COL_X, y: colStartY + colIdx * COL_SPACING },
          data: { name: col.column_name, score: col.column_score, isMain: true },
        });

        assetToColumnNodeIds[assetId].add(colId);
        edges.push(makeEdge(`e-${assetId}->${colId}`, assetId, colId, "#64748b", "asset-to-column"));
      });
    });
  });

  return {
    nodes,
    edges,
    productToAssetNodeIds,
    assetToColumnNodeIds,
    kpiToColumnNodeIds: {},
    kpiToAssetNodeIds: {},
  };
};

const ScoreLegend = () => (
  <div style={{
    position: "absolute", top: 12, right: 12, zIndex: 10,
    background: "rgba(255,255,255,0.95)", border: "1px solid #e2e8f0",
    borderRadius: 10, padding: "10px 16px", display: "flex", gap: 20,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)", fontSize: 11, pointerEvents: "none",
    backdropFilter: "blur(4px)",
  }}>
    {[
      { color: "#ef4444", label: "DQ KPI Score", sub: "< 90%" },
      { color: "#f59e0b", label: "DQ KPI Score", sub: "90% - 95%" },
      { color: "#22c55e", label: "DQ KPI Score", sub: ">= 95%" },
    ].map(({ color, label, sub }) => (
      <div key={sub} style={{ display: "flex", alignItems: "flex-start", gap: 7 }}>
        <div style={{ width: 11, height: 11, borderRadius: "50%", background: color, marginTop: 1, flexShrink: 0 }} />
        <div>
          <div style={{ fontWeight: 600, color: "#374151" }}>{label}</div>
          <div style={{ color: "#6b7280" }}>{sub}</div>
        </div>
      </div>
    ))}
  </div>
);

const KpiLineage = ({ data, loading, assetName, lineageMode = "kpi" }) => {
  const [selectedKpiNodeId, setSelectedKpiNodeId] = useState(null);
  const [selectedProductNodeId, setSelectedProductNodeId] = useState(null);
  const [selectedAssetNodeId, setSelectedAssetNodeId] = useState(null);
  const [expandedProductIds, setExpandedProductIds] = useState(new Set());
  const [expandedKpiIds, setExpandedKpiIds] = useState(new Set());
  const [expandedAssetIds, setExpandedAssetIds] = useState(new Set());
  const [nodePositions, setNodePositions] = useState({});

  const isProductAssetMode = lineageMode === "product-asset-column";

  const graph = useMemo(
    () => (isProductAssetMode ? buildProductAssetColumnGraph(data) : buildKpiLineageGraph(data)),
    [data, isProductAssetMode]
  );

  const allProductNodeIds = useMemo(
    () => graph.nodes.filter((n) => n.type === "kpiProductNode").map((n) => n.id),
    [graph.nodes]
  );

  const allKpiNodeIds = useMemo(
    () => graph.nodes.filter((n) => n.type === "kpiKpiNode").map((n) => n.id),
    [graph.nodes]
  );

  const allAssetNodeIds = useMemo(
    () =>
      graph.nodes
        .filter((n) => n.type === "kpiMainAssetNode" || n.type === "kpiSubAssetNode")
        .map((n) => n.id),
    [graph.nodes]
  );

  const assetToColumnIds = useMemo(() => {
    const map = {};
    graph.edges.forEach((edge) => {
      if (edge?.data?.kind !== "asset-to-column") return;
      if (!map[edge.source]) map[edge.source] = new Set();
      map[edge.source].add(edge.target);
    });
    return map;
  }, [graph.edges]);

  const visibleAssetIds = useMemo(() => {
    const visible = new Set();

    if (isProductAssetMode) {
      expandedProductIds.forEach((productId) => {
        const connectedAssets = graph.productToAssetNodeIds?.[productId] || new Set();
        connectedAssets.forEach((assetId) => visible.add(assetId));
      });
      return visible;
    }

    expandedKpiIds.forEach((kpiId) => {
      const connectedAssets = graph.kpiToAssetNodeIds?.[kpiId] || new Set();
      connectedAssets.forEach((assetId) => visible.add(assetId));
    });
    return visible;
  }, [expandedKpiIds, expandedProductIds, graph.kpiToAssetNodeIds, graph.productToAssetNodeIds, isProductAssetMode]);

  const visibleColumnIds = useMemo(() => {
    const visible = new Set();
    visibleAssetIds.forEach((assetId) => {
      if (!expandedAssetIds.has(assetId)) return;
      const columnIds =
        (isProductAssetMode ? graph.assetToColumnNodeIds?.[assetId] : assetToColumnIds[assetId]) || new Set();
      columnIds.forEach((colId) => visible.add(colId));
    });
    return visible;
  }, [visibleAssetIds, expandedAssetIds, assetToColumnIds, graph.assetToColumnNodeIds, isProductAssetMode]);

  const highlightedColumnIds = useMemo(() => {
    if (isProductAssetMode) {
      if (selectedAssetNodeId) {
        return graph.assetToColumnNodeIds?.[selectedAssetNodeId] || new Set();
      }

      if (selectedProductNodeId) {
        const columns = new Set();
        const assetIds = graph.productToAssetNodeIds?.[selectedProductNodeId] || new Set();
        assetIds.forEach((assetId) => {
          const columnIds = graph.assetToColumnNodeIds?.[assetId] || new Set();
          columnIds.forEach((colId) => columns.add(colId));
        });
        return columns;
      }

      return new Set();
    }
    if (!selectedKpiNodeId) return new Set();
    return graph.kpiToColumnNodeIds?.[selectedKpiNodeId] || new Set();
  }, [
    selectedKpiNodeId,
    selectedProductNodeId,
    selectedAssetNodeId,
    graph.kpiToColumnNodeIds,
    graph.productToAssetNodeIds,
    graph.assetToColumnNodeIds,
    isProductAssetMode,
  ]);

  const highlightedProductIds = useMemo(() => {
    if (!isProductAssetMode || !selectedProductNodeId) return new Set();
    return new Set([selectedProductNodeId]);
  }, [isProductAssetMode, selectedProductNodeId]);

  const highlightedAssetIds = useMemo(() => {
    if (isProductAssetMode) {
      if (selectedAssetNodeId) return new Set([selectedAssetNodeId]);
      if (selectedProductNodeId) return graph.productToAssetNodeIds?.[selectedProductNodeId] || new Set();
      return new Set();
    }
    if (!selectedKpiNodeId) return new Set();
    return graph.kpiToAssetNodeIds?.[selectedKpiNodeId] || new Set();
  }, [selectedKpiNodeId, selectedProductNodeId, selectedAssetNodeId, graph.kpiToAssetNodeIds, graph.productToAssetNodeIds, isProductAssetMode]);

  const allExpanded = isProductAssetMode
    ? allProductNodeIds.length > 0 &&
      expandedProductIds.size === allProductNodeIds.length &&
      expandedAssetIds.size === allAssetNodeIds.length
    : allKpiNodeIds.length > 0 &&
      expandedKpiIds.size === allKpiNodeIds.length &&
      expandedAssetIds.size === allAssetNodeIds.length;

  const nodes = useMemo(() => {
    return graph.nodes.map((node) => {
      const isProduct = node.type === "kpiProductNode";
      const isKpi = node.type === "kpiKpiNode";
      const isAsset = node.type === "kpiMainAssetNode" || node.type === "kpiSubAssetNode";
      const isColumn = node.type === "kpiColumnNode";

      const isVisible =
        (isProductAssetMode ? isProduct : isKpi) ||
        (isAsset && visibleAssetIds.has(node.id)) ||
        (isColumn && visibleColumnIds.has(node.id));

      const isSelectedKpi = node.id === selectedKpiNodeId;
      const highlighted =
        (isProduct && highlightedProductIds.has(node.id)) ||
        (!isProductAssetMode && isSelectedKpi) ||
        (isAsset && highlightedAssetIds.has(node.id)) ||
        (isColumn && highlightedColumnIds.has(node.id));

      const onChevronClick =
        isProduct
          ? () => {
              setExpandedProductIds((prev) => {
                const next = new Set(prev);
                if (next.has(node.id)) next.delete(node.id);
                else next.add(node.id);
                return next;
              });
            }
          : isKpi
          ? () => {
              setExpandedKpiIds((prev) => {
                const next = new Set(prev);
                if (next.has(node.id)) next.delete(node.id);
                else next.add(node.id);
                return next;
              });
            }
          : isAsset
          ? () => {
              setExpandedAssetIds((prev) => {
                const next = new Set(prev);
                if (next.has(node.id)) next.delete(node.id);
                else next.add(node.id);
                return next;
              });
            }
          : undefined;

      return {
        ...node,
        hidden: !isVisible,
        position: nodePositions[node.id] || node.position,
        data: {
          ...node.data,
          highlighted,
          dimmed: false,
          expanded: isProduct
            ? expandedProductIds.has(node.id)
            : isKpi
            ? expandedKpiIds.has(node.id)
            : isAsset
            ? expandedAssetIds.has(node.id)
            : false,
          onChevronClick,
        },
      };
    });
  }, [
    graph.nodes,
    selectedKpiNodeId,
    highlightedProductIds,
    highlightedAssetIds,
    highlightedColumnIds,
    nodePositions,
    visibleAssetIds,
    visibleColumnIds,
    isProductAssetMode,
    expandedProductIds,
    expandedKpiIds,
    expandedAssetIds,
  ]);

  const edges = useMemo(() => {
    return graph.edges.map((edge) => {
      const kind = edge?.data?.kind;
      const isKpiToAsset = kind === "kpi-to-asset";
      const isProductToAsset = kind === "product-to-asset";
      const isAssetToColumn = kind === "asset-to-column";

      const visible =
        (isKpiToAsset && !isProductAssetMode && expandedKpiIds.has(edge.source) && visibleAssetIds.has(edge.target)) ||
        (isProductToAsset && isProductAssetMode && expandedProductIds.has(edge.source) && visibleAssetIds.has(edge.target)) ||
        (isAssetToColumn && expandedAssetIds.has(edge.source) && visibleAssetIds.has(edge.source) && visibleColumnIds.has(edge.target)) ||
        (!isKpiToAsset && !isAssetToColumn && !isProductToAsset);

      if (!visible) {
        return {
          ...edge,
          hidden: true,
          animated: false,
        };
      }

      if (isProductAssetMode) {
        if (!selectedProductNodeId && !selectedAssetNodeId) {
          return {
            ...edge,
            hidden: false,
            animated: false,
          };
        }

        if (selectedProductNodeId) {
          const linkedAssets = graph.productToAssetNodeIds?.[selectedProductNodeId] || new Set();
          const highlightProductEdge = isProductToAsset && edge.source === selectedProductNodeId;
          const highlightColumnEdge = isAssetToColumn && linkedAssets.has(edge.source);

          if (highlightProductEdge || highlightColumnEdge) {
            const color = highlightProductEdge ? "#0f766e" : "#2563eb";
            return {
              ...edge,
              hidden: false,
              style: {
                ...(edge.style || {}),
                stroke: color,
                strokeWidth: 3,
                strokeDasharray: highlightColumnEdge ? "2 2" : "",
                opacity: 1,
              },
              markerEnd: { ...(edge.markerEnd || {}), color, width: 16, height: 16 },
              animated: true,
            };
          }

          return {
            ...edge,
            hidden: false,
            animated: false,
          };
        }

        const highlight = isAssetToColumn && edge.source === selectedAssetNodeId;
        if (highlight) {
          return {
            ...edge,
            hidden: false,
            style: {
              ...(edge.style || {}),
              stroke: "#2563eb",
              strokeWidth: 3,
              strokeDasharray: "2 2",
              opacity: 1,
            },
            markerEnd: { ...(edge.markerEnd || {}), color: "#2563eb", width: 16, height: 16 },
            animated: true,
          };
        }

        return {
          ...edge,
          hidden: false,
          animated: false,
        };
      }

      if (!selectedKpiNodeId) {
        return {
          ...edge,
          hidden: false,
          animated: false,
        };
      }

      const highlight =
        (isKpiToAsset && edge.source === selectedKpiNodeId) ||
        (isAssetToColumn && highlightedColumnIds.has(edge.target));

      if (highlight) {
        const color = isAssetToColumn ? "#2563eb" : "#15803d";
        return {
          ...edge,
          hidden: false,
          style: {
            ...(edge.style || {}),
            stroke: color,
            strokeWidth: 3,
            strokeDasharray: isAssetToColumn ? "2 2" : "",
            opacity: 1,
          },
          markerEnd: { ...(edge.markerEnd || {}), color, width: 16, height: 16 },
          animated: true,
        };
      }

      return {
        ...edge,
        hidden: false,
        animated: false,
      };
    });
  }, [
    graph.edges,
    selectedProductNodeId,
    selectedAssetNodeId,
    selectedKpiNodeId,
    highlightedColumnIds,
    graph.productToAssetNodeIds,
    visibleAssetIds,
    visibleColumnIds,
    isProductAssetMode,
    expandedProductIds,
    expandedKpiIds,
    expandedAssetIds,
  ]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center" style={{ height: "80vh" }}>
        <img src={heartLoading} alt="Loading..." style={{ width : 200}} />
        <span className="text-gray-700 text-[20px] mt-3">
          Please wait while loading KPI lineage...
        </span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center" style={{ height: "60vh" }}>
        <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M9 17v-2m3 2v-4m3 4v-6M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <p className="text-gray-500 text-lg font-medium">No KPI Lineage Data Available</p>
        <p className="text-gray-400 text-sm mt-1">No lineage information found for this asset.</p>
      </div>
    );
  }

  const fullScreenUrl = assetName
    ? `/kpi-lineage-fullscreen?assetName=${encodeURIComponent(assetName)}`
    : null;

  return (
    <div style={{ width: "100%", height: "80vh", minHeight: "30vh", position: "relative", padding: "32px 28px 24px 28px", boxSizing: "border-box" }}>
      <div
        style={{
          position: "absolute",
          left: 12,
          top: 12,
          zIndex: 11,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <button
          onClick={() => {
            if (allExpanded) {
              if (isProductAssetMode) {
                setExpandedProductIds(new Set());
              } else {
                setExpandedKpiIds(new Set());
              }
              setExpandedKpiIds(new Set());
              setExpandedAssetIds(new Set());
              return;
            }

            if (isProductAssetMode) {
              setExpandedProductIds(new Set(allProductNodeIds));
            } else {
              setExpandedKpiIds(new Set(allKpiNodeIds));
            }
            setExpandedAssetIds(new Set(allAssetNodeIds));
          }}
          title={allExpanded ? "Collapse all" : "Expand all"}
          aria-label={allExpanded ? "Collapse all" : "Expand all"}
          style={{
            background: "#ffffff",
            color: "#1f2937",
            border: "1px solid #cbd5e1",
            borderRadius: 999,
            padding: 0,
            width: 34,
            height: 34,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            cursor: "pointer",
          }}
        >
          {allExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <div
          style={{
            background: "#ecfdf5",
            color: "#065f46",
            border: "1px solid #a7f3d0",
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 600,
            padding: "6px 10px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            whiteSpace: "nowrap",
          }}
        >
          Click a node to see its connections
        </div>
      </div>
      <ScoreLegend />
      {fullScreenUrl && (
        <a
          href={fullScreenUrl}
          target="_self"
          title="View in full screen"
          style={{
            position: "absolute", top: 12, left: 12, zIndex: 10,
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "#eff6ff", border: "1px solid #bfdbfe",
            borderRadius: 8, padding: "6px 12px",
            fontSize: 11, fontWeight: 600, color: "#1d4ed8",
            textDecoration: "none", boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            transition: "background 0.15s",
          }}
        >
          <Maximize2 size={13} />
          Full screen
        </a>
      )}
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onPaneClick={() => {
            setSelectedKpiNodeId(null);
            setSelectedProductNodeId(null);
            setSelectedAssetNodeId(null);
          }}
          onNodeDragStop={(_, node) => {
            setNodePositions((prev) => ({ ...prev, [node.id]: node.position }));
          }}
          onNodeClick={(_, node) => {
            if (isProductAssetMode) {
              if (node.type === "kpiProductNode") {
                setSelectedProductNodeId((prev) => (prev === node.id ? null : node.id));
                setSelectedAssetNodeId(null);
                return;
              }
              if (node.type !== "kpiMainAssetNode") return;
              setSelectedAssetNodeId((prev) => (prev === node.id ? null : node.id));
              setSelectedProductNodeId(null);
              return;
            }
            if (node.type !== "kpiKpiNode") return;
            setSelectedKpiNodeId((prev) => (prev === node.id ? null : node.id));
          }}
          nodeTypes={kpiNodeTypes}
          style={{ background: "#fdfcff" }}
          defaultViewport={{ x: 84, y: 72, zoom: 0.8 }}
          minZoom={0.1}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
        >
          <Controls position="bottom-right" showZoom showFitView showInteractive style={{ right: 200 }} />
          <MiniMap
            position="bottom-left"
            nodeColor={(n) =>
              n.type === "kpiProductNode"
                ? "#0f766e"
                : n.type === "kpiMainAssetNode"
                ? "#16a34a"
                : n.type === "kpiKpiNode"
                ? "#1e40af"
                : "#60a5fa"
            }
          />
          <Background variant={BackgroundVariant.Dots} gap={16} size={1.2} color="#f2d7f7" />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
};

export default KpiLineage;
